/**
 * Contador regressivo. O mesmo card do progresso da manutenção,
 * com barra, percentual e relógio. Palco, visualização e Moodle usam este HTML.
 */

function escText(value) {
  return String(value ?? '').replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
}

function escAttr(value) {
  return escText(value).replace(/"/g, '&quot;');
}

function safeColor(value, fallback) {
  const v = String(value || '').trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return v;
  return fallback;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toLocalInput(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function defaultSchedule() {
  const start = new Date();
  start.setSeconds(0, 0);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  return { startsAt: toLocalInput(start), endsAt: toLocalInput(end) };
}

function instantFromInput(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function formatSchedule(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return '';
  return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`;
}

function safeId(widgetId) {
  return String(widgetId || 'cd').replace(/[^a-zA-Z0-9_-]/g, '') || 'cd';
}

/** Atualiza barra, percentual e relógio. O script publicado usa esta mesma função. */
export function paintCountdown(root, nowMs) {
  if (!root) return;
  const start = Number(root.getAttribute('data-start')) || 0;
  const end = Number(root.getAttribute('data-end')) || 0;
  const now = Number(nowMs) || Date.now();
  let percent = 0;
  let left = 0;
  if (end > start && start > 0) {
    percent = Math.round(Math.max(0, Math.min(100, ((now - start) / (end - start)) * 100)));
  } else if (end > 0 && now >= end) {
    percent = 100;
  }
  if (end > now) left = Math.floor((end - now) / 1000);
  const hours = Math.floor(left / 3600);
  const minutes = Math.floor((left % 3600) / 60);
  const seconds = left % 60;
  const pad = function (n) { return String(n).padStart(2, '0'); };
  const hoursEl = root.querySelector('[data-cd-h]');
  const minsEl = root.querySelector('[data-cd-m]');
  const secsEl = root.querySelector('[data-cd-s]');
  const pctEl = root.querySelector('[data-cd-pct]');
  const barEl = root.querySelector('[data-cd-bar]');
  const hintEl = root.querySelector('[data-cd-hint]');
  if (hoursEl) hoursEl.textContent = pad(hours);
  if (minsEl) minsEl.textContent = pad(minutes);
  if (secsEl) secsEl.textContent = pad(seconds);
  if (pctEl) pctEl.textContent = percent + '%';
  if (barEl) barEl.style.width = percent + '%';
  if (hintEl) {
    const done = root.getAttribute('data-done') || '';
    const live = root.getAttribute('data-live') || '';
    hintEl.textContent = (percent >= 100 && done) ? done : live;
  }
}

const COUNTDOWN_CSS = `
.madri-cd{--cd-card:#ffffff;--cd-text:#0b1c30;--cd-muted:#464555;--cd-accent:#4f46e5;--cd-accent2:#0891b2;--cd-track:#e5eeff;box-sizing:border-box;width:100%;max-width:100%;margin:0;background:var(--cd-card);color:var(--cd-text) !important;border:1px solid var(--cd-track);border-radius:20px;padding:20px;box-shadow:0 16px 40px -18px rgba(15,23,42,.18);font-family:Inter,Segoe UI,sans-serif}
.madri-cd *{box-sizing:border-box}
.madri-cd__row{display:flex;justify-content:space-between;align-items:flex-end;gap:12px}
.madri-cd__kicker{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--cd-muted) !important}
.madri-cd__hint{margin-top:4px;font-size:12px;line-height:1.4;color:var(--cd-muted) !important}
.madri-cd__pct{font-family:"Plus Jakarta Sans",Inter,sans-serif;font-size:28px;font-weight:700;color:var(--cd-accent) !important;line-height:1}
.madri-cd__track{margin-top:14px;height:12px;border-radius:999px;background:var(--cd-track);overflow:hidden;padding:2px}
.madri-cd__bar{height:100%;border-radius:999px;background:linear-gradient(90deg,var(--cd-accent),var(--cd-accent2));width:0}
.madri-cd__meta{display:flex;justify-content:space-between;gap:8px;margin-top:10px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:11px;color:var(--cd-muted) !important}
.madri-cd__clock{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:14px}
.madri-cd__tick{text-align:center;border-radius:12px;padding:10px 6px;background:var(--cd-track)}
.madri-cd__tick b{display:block;font-size:22px;line-height:1.1;font-weight:700;font-family:"Plus Jakarta Sans",Inter,sans-serif;color:var(--cd-text) !important}
.madri-cd__tick--mid b{color:var(--cd-accent) !important}
.madri-cd__tick span{display:block;margin-top:4px;font-size:10px;letter-spacing:.05em;text-transform:uppercase;color:var(--cd-muted) !important}
`.replace(/\s+/g, ' ').trim();

function bootScript(id) {
  return `<script>(function(){
  var paint=${paintCountdown.toString()};
  var root=document.getElementById(${JSON.stringify(id)});
  if(!root||root.getAttribute('data-cd-ready')==='1') return;
  root.setAttribute('data-cd-ready','1');
  var origin=Date.now();
  var server=origin;
  if(typeof window.geppettoServerNow==='number' && window.geppettoServerNow>1000000000){
    server=window.geppettoServerNow*1000;
  }
  function tick(){ paint(root, server+(Date.now()-origin)); }
  tick();
  setInterval(tick,1000);
})();</script>`;
}

export function createDefaultCountdownContent(text = {}) {
  return {
    progressLabel: text.progressLabel || 'Progresso da atualização',
    progressHint: text.progressHint || 'Otimização e migração',
    doneLabel: text.doneLabel || 'Concluído',
    ...defaultSchedule(),
    forecastLabel: text.forecastLabel || 'Previsão',
    showProgress: true,
    showCountdown: true,
    hoursLabel: text.hoursLabel || 'Horas',
    minutesLabel: text.minutesLabel || 'Minutos',
    secondsLabel: text.secondsLabel || 'Segundos',
    cardColor: '#ffffff',
    cardText: '#0b1c30',
    mutedColor: '#464555',
    accentColor: '#4f46e5',
    accent2Color: '#0891b2',
    trackColor: '#e5eeff',
  };
}

export function buildCountdownInnerHtml(content, widgetId, options = {}) {
  const base = createDefaultCountdownContent();
  const c = { ...base, ...(content || {}) };
  const id = `madri-cd-${safeId(widgetId)}`;
  const startMs = instantFromInput(c.startsAt);
  const endMs = instantFromInput(c.endsAt);
  const now = Date.now();
  let percent = 0;
  let left = 0;
  if (endMs > startMs && startMs > 0) {
    percent = Math.round(Math.max(0, Math.min(100, ((now - startMs) / (endMs - startMs)) * 100)));
  } else if (endMs > 0 && now >= endMs) {
    percent = 100;
  }
  if (endMs > now) left = Math.floor((endMs - now) / 1000);
  const hours = Math.floor(left / 3600);
  const minutes = Math.floor((left % 3600) / 60);
  const seconds = left % 60;
  const showProgress = c.showProgress !== false;
  const showCountdown = c.showCountdown !== false;
  const liveHint = String(c.progressHint || '');
  const doneLabel = String(c.doneLabel || '');
  const hintText = percent >= 100 && doneLabel ? doneLabel : liveHint;
  const forecast = formatSchedule(c.endsAt);
  const card = safeColor(c.cardColor, '#ffffff');
  const text = safeColor(c.cardText, '#0b1c30');
  const muted = safeColor(c.mutedColor, '#464555');
  const accent = safeColor(c.accentColor, '#4f46e5');
  const accent2 = safeColor(c.accent2Color, '#0891b2');
  const track = safeColor(c.trackColor, '#e5eeff');
  const vars = [
    `--cd-card:${card}`,
    `--cd-text:${text}`,
    `--cd-muted:${muted}`,
    `--cd-accent:${accent}`,
    `--cd-accent2:${accent2}`,
    `--cd-track:${track}`,
  ].join(';');
  const progress = showProgress ? `<div class="madri-cd__row"><div><div class="madri-cd__kicker">${escText(c.progressLabel)}</div><div class="madri-cd__hint" data-cd-hint>${escText(hintText)}</div></div><div class="madri-cd__pct" data-cd-pct>${percent}%</div></div><div class="madri-cd__track"><div class="madri-cd__bar" data-cd-bar style="width:${percent}%"></div></div>` : '';
  const hintOnly = !showProgress && (liveHint || doneLabel) ? `<div class="madri-cd__hint" data-cd-hint>${escText(hintText)}</div>` : '';
  const meta = forecast ? `<div class="madri-cd__meta"><span>${escText(c.forecastLabel)}: ${escText(forecast)}</span></div>` : '';
  const clock = showCountdown ? `<div class="madri-cd__clock"><div class="madri-cd__tick"><b data-cd-h>${pad2(hours)}</b><span>${escText(c.hoursLabel)}</span></div><div class="madri-cd__tick madri-cd__tick--mid"><b data-cd-m>${pad2(minutes)}</b><span>${escText(c.minutesLabel)}</span></div><div class="madri-cd__tick"><b data-cd-s>${pad2(seconds)}</b><span>${escText(c.secondsLabel)}</span></div></div>` : '';
  const script = options.stage ? '' : bootScript(id);
  return `<style>${COUNTDOWN_CSS}</style>${script}<div id="${id}" class="madri-cd" style="${escAttr(vars)}" data-start="${startMs}" data-end="${endMs}" data-live="${escAttr(liveHint)}" data-done="${escAttr(doneLabel)}">${progress}${hintOnly}${meta}${clock}</div>`;
}
